import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { UseQueryResult } from "@tanstack/react-query";
import { BulkTest, WebSocketEventType } from "@/model";
import { compareDate } from "@/util/dateUtil";
import { useWebsocket } from "@/hook/useWebsocket";
import { errorToast } from "@/component/Toast";


export const useTestProgressHandler = (refetch: () => Promise<UseQueryResult<BulkTest[], Error>>) => {
    const intl = useIntl();
    const { addEventListener, removeEventListener } = useWebsocket();

    const [testingId, setTestingId] = useState<string | undefined>(undefined);
    const testingIdRef = useRef<string | undefined>(undefined);
    const [testingProgress, setTestingProgress] = useState<number>(0);

    const findTesting = useCallback((totalCount: number, bulkTests?: BulkTest[]) => {
        return bulkTests?.filter(test => test.status === "progress" && totalCount === test.test_case_count)
                        .sort((a, b) => compareDate(a.created_at, b.created_at, "desc"))[0];
    }, []);

    const handleBulkTestStarted = useCallback(({ data }: {data: { total: number }}) => {
        const { total: totalCount } = data;
        setTestingProgress(0);
        refetch()
            .then(({ data }) => {
                const testingId = findTesting(totalCount, data)?.id;
                testingIdRef.current = testingId;
                setTestingId(testingId);
            });
    }, [refetch, findTesting]);

    const handleBulkTestProgress = useCallback(({ data }: {data: { total: number, index?: number }}) => {
        const { total, index } = data;
        setTestingProgress((index && total) ? index / total * 100 : 0);

        if (!testingIdRef.current) {
            refetch()
                .then(({ data }) => {
                    const testingId = findTesting(total, data)?.id;
                    testingIdRef.current = testingId;
                    setTestingId(testingId);
                });
        }
    }, [refetch, findTesting]);

    const handleBulkTestFinished = useCallback(() => {
        setTestingId(undefined);
        setTestingProgress(0);
        refetch();
    }, [refetch]);

    const handleBulkTestCanceled = useCallback((data: { reason: "NO_QUERY" | "USER_CANCEL" | "BROKEN_FILE" }) => {
        setTestingProgress(0);
        if (data.reason === "NO_QUERY") {
            errorToast(intl.formatMessage({ id: "i05010" }));
        } else if (data.reason === "USER_CANCEL") {
            errorToast(intl.formatMessage({ id: "i05011" }));
        } else if (data.reason === "BROKEN_FILE") {
            errorToast(intl.formatMessage({ id: "i05012" }));
        }
        setTestingId(undefined);
        setTestingProgress(0);
        refetch();
    }, [refetch, intl]);

    useEffect(() => {
        const listeners: { event: WebSocketEventType, handler: (data?: any) => void }[] = [
            { event: "bulk_test_started", handler: handleBulkTestStarted },
            { event: "bulk_test_progress", handler: handleBulkTestProgress },
            { event: "bulk_test_finished", handler: handleBulkTestFinished },
            { event: "bulk_test_canceled", handler: handleBulkTestCanceled }
        ];

        listeners.forEach(({ event, handler }) => {
            addEventListener(event, handler);
        });

        return () => {
            listeners.forEach(({ event, handler }) => {
                removeEventListener(event, handler);
            });
        };
    }, [addEventListener, removeEventListener, handleBulkTestStarted, handleBulkTestProgress, handleBulkTestCanceled, handleBulkTestFinished]);

    return { testingId, testingProgress };
};
